// ----------------------------------------------------------------------------
// Quantity Selector Extension (Validate Hardcoded Quantity Limits)
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import translate from 'scripts/helpers/iamota-translate';
import Debug from 'scripts/libs/debug';

// -------------------------------------
// Functionality
// -------------------------------------

// Constants (set to null to disable)
var MAX_QUANTITY = 8;
var MIN_QUANTITY = null;
var STEP_LIMIT = null; // e.g. 5 to require users to purchase in increments of 5

var rebuyCartData = null;

// Cache validation context per quantity input and cart data snapshot
var validationContextCache = new WeakMap();
var getValidationContext = quantityInput => {
  var variantId = getVariantIdFromForm(quantityInput);
  var sellingPlanId = getSellingPlanIdFromForm(quantityInput);
  var cached = validationContextCache.get(quantityInput);
  if (cached && cached.variantId === variantId && cached.sellingPlanId === sellingPlanId && cached.cartDataRef === rebuyCartData) {
    return {
      variantId: cached.variantId,
      sellingPlanId: cached.sellingPlanId,
      existingCartQuantity: cached.existingCartQuantity
    };
  }
  var existingCartQuantity = getExistingCartQuantity(variantId, sellingPlanId);
  validationContextCache.set(quantityInput, {
    variantId,
    sellingPlanId,
    existingCartQuantity,
    cartDataRef: rebuyCartData
  });
  return {
    variantId,
    sellingPlanId,
    existingCartQuantity
  };
};
var getRequestedQuantity = current_quantity => {
  var parsedQuantity = parseInt(current_quantity, 10);
  return Number.isNaN(parsedQuantity) ? 0 : parsedQuantity;
};
var getProductFormElement = quantityInput => {
  return (quantityInput === null || quantityInput === void 0 ? void 0 : quantityInput.closest('iam-product-form')) || (quantityInput === null || quantityInput === void 0 ? void 0 : quantityInput.closest('form')) || null;
};
var getSellingPlanIdFromForm = quantityInput => {
  var formElement = getProductFormElement(quantityInput);
  if (!formElement) return null;
  var sellingPlanInput = formElement.querySelector('input[name="selling_plan"]');
  return sellingPlanInput !== null && sellingPlanInput !== void 0 && sellingPlanInput.value ? String(sellingPlanInput.value) : null;
};
var getVariantIdFromForm = quantityInput => {
  var formElement = getProductFormElement(quantityInput);
  if (!formElement) return 0;
  var variantInput = formElement.querySelector('input[name="id"]');
  return Number((variantInput === null || variantInput === void 0 ? void 0 : variantInput.value) || 0);
};
var getSellingPlanIdFromCartItem = item => {
  var _item$selling_plan_al;
  return item !== null && item !== void 0 && (_item$selling_plan_al = item.selling_plan_allocation) !== null && _item$selling_plan_al !== void 0 && (_item$selling_plan_al = _item$selling_plan_al.selling_plan) !== null && _item$selling_plan_al !== void 0 && _item$selling_plan_al.id ? String(item.selling_plan_allocation.selling_plan.id) : item !== null && item !== void 0 && item.selling_plan ? String(item.selling_plan) : null;
};
var getRebuyCartItems = () => {
  var _rebuyCartData;
  return ((_rebuyCartData = rebuyCartData) === null || _rebuyCartData === void 0 ? void 0 : _rebuyCartData.items) || [];
};
var getExistingCartQuantity = function getExistingCartQuantity(variantId) {
  var sellingPlanId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  if (!variantId) return 0;
  return getRebuyCartItems().reduce((total, item) => {
    var _item$variant;
    var cartVariantId = Number((item === null || item === void 0 ? void 0 : item.variant_id) || (item === null || item === void 0 || (_item$variant = item.variant) === null || _item$variant === void 0 ? void 0 : _item$variant.id) || (item === null || item === void 0 ? void 0 : item.id) || 0);
    var cartSellingPlanId = getSellingPlanIdFromCartItem(item);
    var isSameVariant = cartVariantId === Number(variantId);
    var isSameSellingPlan = String(cartSellingPlanId || '') === String(sellingPlanId || '');
    if (isSameVariant && isSameSellingPlan) {
      return total + Number((item === null || item === void 0 ? void 0 : item.quantity) || 0);
    }
    return total;
  }, 0);
};
var refreshQuantityValidators = () => {
  document.querySelectorAll('iam-product-form select[name="quantity"], form select[name="quantity"]').forEach(select => {
    select.dispatchEvent(new Event('change', {
      bubbles: true
    }));
    select.dispatchEvent(new Event('input', {
      bubbles: true
    }));
  });
};
document.addEventListener('rebuy:cart.change', event => {
  var _rebuyCartData2, _rebuyCartData3, _rebuyCartData4;
  var {
    cart
  } = event.detail || {};
  var updatedCartData = cart === null || cart === void 0 ? void 0 : cart.cart;
  if (!updatedCartData) {
    Debug.extreme('Rebuy cart change event received without cart data; keeping existing cart state', {
      hasExistingCartData: Boolean(rebuyCartData)
    });
    return;
  }
  rebuyCartData = updatedCartData;
  Debug.extreme('Rebuy cart updated', {
    item_count: (_rebuyCartData2 = rebuyCartData) === null || _rebuyCartData2 === void 0 ? void 0 : _rebuyCartData2.item_count,
    total_price: (_rebuyCartData3 = rebuyCartData) === null || _rebuyCartData3 === void 0 ? void 0 : _rebuyCartData3.total_price,
    items: ((_rebuyCartData4 = rebuyCartData) === null || _rebuyCartData4 === void 0 ? void 0 : _rebuyCartData4.items) || []
  });
  refreshQuantityValidators();
});

// Re-run validation when selling plan changes
iamotaEventHandler.subscribe('productForm:sellingPlan:changed', () => {
  refreshQuantityValidators();
});
var validationRules = {
  max: {
    condition_value: (_, itemData, quantityInput) => {
      var _itemData$product;
      if (itemData !== null && itemData !== void 0 && (_itemData$product = itemData.product) !== null && _itemData$product !== void 0 && (_itemData$product = _itemData$product.tags) !== null && _itemData$product !== void 0 && _itemData$product.includes('Free Trial')) return 1;
      if (quantityInput !== null && quantityInput !== void 0 && quantityInput.hasAttribute('data-pdp-quantity-selector')) return null;
      return MAX_QUANTITY;
    },
    condition_callback: function condition_callback(current_quantity, max_quantity, itemData) {
      var context = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      var existingCartQuantity = context.existingCartQuantity || 0;
      var totalQuantity = existingCartQuantity + current_quantity;
      return totalQuantity <= max_quantity;
    },
    error_message: function error_message(current_quantity, max_quantity, itemData) {
      var context = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      var existingCartQuantity = context.existingCartQuantity || 0;
      var totalQuantity = existingCartQuantity + current_quantity;
      return translate('You can only add up to {{ MAX_QUANTITY }} of {{ itemData.variant.name }}.', 'quantity.error.limit_max', {
        QUANTITY: totalQuantity,
        MAX_QUANTITY: max_quantity,
        EXISTING_QUANTITY: existingCartQuantity,
        itemData
      });
    }
  },
  min: {
    condition_value: () => MIN_QUANTITY,
    condition_callback: (current_quantity, min_quantity) => current_quantity === 0 || current_quantity >= min_quantity,
    error_message: (current_quantity, min_quantity, itemData) => translate('You must add at least {{ MIN_QUANTITY }} of {{ itemData.variant.name }}.', 'quantity.error.limit_min', {
      QUANTITY: current_quantity,
      MIN_QUANTITY: min_quantity,
      itemData
    })
  },
  step: {
    condition_value: () => STEP_LIMIT,
    condition_callback: (current_quantity, step) => current_quantity % step === 0,
    error_message: (current_quantity, step, itemData) => translate('You must buy {{ itemData.variant.name }} in increments of {{ STEP }}.', 'quantity.error.limit_step', {
      QUANTITY: current_quantity,
      STEP: step,
      itemData
    })
  }
};
iamotaEventHandler.subscribe('iamForm:quantity:validation:init', _ref => {
  var {
    iamFormElement,
    validator
  } = _ref;
  var quantityInput = iamFormElement;
  var _loop = function _loop(ruleType) {
    var rule = validationRules[ruleType];
    validator.addField(quantityInput.control, [{
      validator: (current_quantity, fields) => {
        var itemData = quantityInput.getItemData();
        var parsedQuantity = getRequestedQuantity(current_quantity);
        var {
          variantId,
          sellingPlanId,
          existingCartQuantity
        } = getValidationContext(quantityInput);
        var condition_value = rule.condition_value(parsedQuantity, itemData, quantityInput);
        var context = {
          variantId,
          sellingPlanId,
          existingCartQuantity
        };
        var result = condition_value === null || rule.condition_callback(parsedQuantity, condition_value, itemData, context);
        Debug.extreme("Validating Quantity [".concat(ruleType, "]: ").concat(parsedQuantity, " vs. ").concat(condition_value, " => ").concat(result ? Debug.colors.bgGreen('Valid') : Debug.colors.bgRed('Invalid')), {
          current_quantity: parsedQuantity,
          condition_value,
          variantId,
          sellingPlanId,
          existingCartQuantity,
          totalQuantity: existingCartQuantity + parsedQuantity,
          result,
          itemData,
          fields
        });
        return result;
      },
      errorMessage: (current_quantity, fields) => {
        var itemData = quantityInput.getItemData();
        var parsedQuantity = getRequestedQuantity(current_quantity);
        var {
          variantId,
          sellingPlanId,
          existingCartQuantity
        } = getValidationContext(quantityInput);
        var condition_value = rule.condition_value(parsedQuantity, itemData, quantityInput);
        var context = {
          variantId,
          sellingPlanId,
          existingCartQuantity
        };
        Debug.extreme("Quantity validation error [".concat(ruleType, "]"), {
          current_quantity: parsedQuantity,
          condition_value,
          variantId,
          sellingPlanId,
          existingCartQuantity,
          itemData,
          fields
        });
        return rule.error_message(parsedQuantity, condition_value, itemData, context);
      }
    }], {
      errorsContainer: quantityInput.errorContainer
    });
  };
  for (var ruleType in validationRules) {
    _loop(ruleType);
  }
}, {
  source: "quantity-input--hardcoded-limits.js"
});